import {Box, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import React, {useMemo} from 'react'
import headingSlotRecipe from './index.recipe'
import {hasValidString} from '../../../utils/utils'
import RichText from '../rich-text'

/**
 * Renders a heading with optional subline and customizable alignment.
 * Intended for use from the leap modules as top level heading. Should not be used by module children.
 * Extend the variants if needed.
 * Purpose is to have all module heading handled by this component.
 * If needed to use different alignment per breakpoint use alignment=inherit and apply textAlign on parent element.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {1|2|3|4|5|6} props.headingLevel - HTML heading level (1–6) used to determine the `<h*>` tag.
 * @param {string} props.headline1 - Main heading text first part.
 * @param {string} props.headline2 - Main heading text second part.
 * @param {string} [props.subline] - Optional secondary text displayed below the headline.
 * @param {'left'|'center'|'inherit'} [props.alignment='left'] - Horizontal alignment of the heading content, could be used a chakra breakpoint object
 * @param {boolean} [props.separateLines=false] - Whether headline1 and headline2 render on separate lines
 * @param {boolean} [props.dualTone=false] - Whether headline2 uses secondary color (text.secondary) vs primary
 * @param {('default'|'awards'|'hero'|'hidden')} [props.variant='default'] - Visual variant: 'default' uses display.small, 'awards' uses heading.large, 'hero' uses display.largeItalic, 'hidden' collapses element space
 * @returns {JSX.Element} A heading element with an optional subline.
 */

const VALID_HEADLINE_SIZES = ['small', 'medium', 'large']

const Heading = ({
    headingLevel,
    headline1,
    headline2,
    separateLines = false,
    dualTone = false,
    subline,
    alignment = 'left',
    variant = 'default',
    headlineSize
}) => {
    const effectiveHeadlineSize = VALID_HEADLINE_SIZES.includes(headlineSize)
        ? headlineSize
        : 'small'
    const recipe = useSlotRecipe({recipe: headingSlotRecipe})
    const styles = useMemo(
        () =>
            recipe({
                alignment,
                dualTone,
                separateLines,
                variant,
                headlineSize: effectiveHeadlineSize
            }),
        [recipe, alignment, dualTone, separateLines, variant, effectiveHeadlineSize]
    )

    if (variant === 'hidden') return null

    const validHeadline1 = hasValidString(headline1)
    const validHeadline2 = hasValidString(headline2)

    if (!validHeadline1 && !validHeadline2 && !hasValidString(subline)) return null

    const headline1Text = headline1 + (validHeadline2 && !separateLines ? '\u00A0' : '')

    return (
        <Box as={`h${headingLevel}`} css={styles.root}>
            {subline && (
                <RichText as="span" css={styles.subline}>
                    {subline}
                </RichText>
            )}
            {validHeadline1 && (
                <Box as="span" css={styles.headline}>
                    <RichText as="span" css={styles.headline1}>
                        {headline1Text}
                    </RichText>
                    {validHeadline2 && (
                        <RichText as="span" css={styles.headline2}>
                            {headline2}
                        </RichText>
                    )}
                </Box>
            )}
        </Box>
    )
}

Heading.propTypes = {
    headingLevel: PropTypes.number,
    subline: PropTypes.string,
    alignment: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.oneOf(['left', 'center', 'inherit'])
    ]),
    headline1: PropTypes.string,
    headline2: PropTypes.string,
    separateLines: PropTypes.bool,
    dualTone: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'awards', 'hero', 'hidden']),
    headlineSize: PropTypes.oneOf(['small', 'medium', 'large'])
}

export default Heading
